import React, { useState, useEffect } from "react";
import { API_URL } from "../../config/api";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function AssessmentExercise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quiz_id = searchParams.get("quiz_id");
  const schedule_id = searchParams.get("schedule_id");
  console.log("Schedule ID:", schedule_id);
  const [quiz, setQuiz] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(1);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [matchingAnswers, setMatchingAnswers] = useState({});
  const [shuffledMatchingOptions, setShuffledMatchingOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);

  // Fetch quiz data
  useEffect(() => {
    if (!quiz_id) {
      setError("No quiz ID provided");
      setLoading(false);
      return;
    }

    const fetchQuiz = async () => {
      try {
        setLoading(true);
        const token =
          localStorage.getItem("token") || localStorage.getItem("access_token");

        const response = await fetch(`${API_URL}/quiz/full_quiz/${quiz_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setQuiz(data);

        // Initialize shuffled options for matching questions
        const shuffled = {};
        data.questions.forEach((question) => {
          if (
            question.question_type === "MATCHING" &&
            question.matching_pairs
          ) {
            const rightOptions = question.matching_pairs.map((pair) => ({
              position: pair.position,
              text: pair.right_text,
            }));
            // Shuffle the right options
            shuffled[question.position] = rightOptions.sort(
              () => Math.random() - 0.5,
            );
          }
        });
        setShuffledMatchingOptions(shuffled);

        // Set timer if quiz has duration
        if (data.time_limit_seconds) {
          setTimeRemaining(data.time_limit_seconds);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching quiz:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [quiz_id]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0 || submitted) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmitQuiz(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, submitted]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return { minutes: mins, seconds: secs };
  };

  // Handle matching answer
  const handleMatchingAnswer = (
    questionPosition,
    leftPosition,
    rightPosition,
  ) => {
    setMatchingAnswers({
      ...matchingAnswers,
      [questionPosition]: {
        ...matchingAnswers[questionPosition],
        [leftPosition]: rightPosition,
      },
    });
  };

  // Toggle answer for multiple choice
  const toggleAnswer = (questionPosition, optionPosition) => {
    const currentQuestion = quiz.questions.find(
      (q) => q.position === questionPosition,
    );
    const current = selectedAnswers[questionPosition] || [];

    if (currentQuestion.question_type === "MULTIPLE_CHOICE") {
      if (current.includes(optionPosition)) {
        setSelectedAnswers({
          ...selectedAnswers,
          [questionPosition]: current.filter((id) => id !== optionPosition),
        });
      } else {
        setSelectedAnswers({
          ...selectedAnswers,
          [questionPosition]: [...current, optionPosition],
        });
      }
    } else {
      setSelectedAnswers({
        ...selectedAnswers,
        [questionPosition]: [optionPosition],
      });
    }
  };

  // Handle text answer
  const handleTextAnswer = (questionPosition, value) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionPosition]: [value],
    });
  };

  // Check if question is answered
  const isQuestionAnswered = (position, questionType) => {
    if (questionType === "MATCHING") {
      const userMatches = matchingAnswers[position] || {};
      const question = quiz.questions.find((q) => q.position === position);
      return (
        Object.keys(userMatches).length === question?.matching_pairs?.length
      );
    }
    return selectedAnswers[position] && selectedAnswers[position].length > 0;
  };

  // Calculate score
  const calculateScore = () => {
    let totalScore = 0;
    let correctAnswers = 0;
    const questionResults = [];

    quiz.questions.forEach((question) => {
      let isCorrect = false;

      if (question.question_type === "MATCHING") {
        const userMatches = matchingAnswers[question.position] || {};
        const correctMatches = {};

        question.matching_pairs.forEach((pair) => {
          correctMatches[pair.position] = pair.position;
        });

        const allCorrect = question.matching_pairs.every((pair) => {
          return userMatches[pair.position] === pair.position;
        });

        const allAnswered =
          Object.keys(userMatches).length === question.matching_pairs.length;

        isCorrect = allCorrect && allAnswered;

        questionResults.push({
          question_id: question.question_id,
          position: question.position,
          is_correct: isCorrect,
          user_answers: userMatches,
          correct_answer: correctMatches,
        });
      } else {
        const userAnswers = selectedAnswers[question.position] || [];

        if (
          question.question_type === "MULTIPLE_CHOICE" ||
          question.question_type === "SINGLE_CHOICE" ||
          question.question_type === "TRUE_FALSE"
        ) {
          const correctOptions = question.options
            .filter((opt) => opt.is_correct)
            .map((opt) => opt.position);

          const userAnswersSorted = [...userAnswers].sort();
          const correctOptionsSorted = [...correctOptions].sort();

          isCorrect =
            JSON.stringify(userAnswersSorted) ===
            JSON.stringify(correctOptionsSorted);
        } else if (
          ["SHORT_ANSWER", "FILL_IN_THE_BLANK"].includes(question.question_type)
        ) {
          const userAnswer = (userAnswers[0] || "").toLowerCase().trim();
          const correctAnswer = (question.correct_answer || "")
            .toLowerCase()
            .trim();
          isCorrect = userAnswer === correctAnswer;
        }

        questionResults.push({
          question_id: question.question_id,
          position: question.position,
          is_correct: isCorrect,
          user_answers: userAnswers,
          correct_answer:
            question.correct_answer ||
            question.options
              ?.filter((o) => o.is_correct)
              .map((o) => o.position),
        });
      }

      if (isCorrect) {
        totalScore += question.points;
        correctAnswers++;
      }
    });

    console.log("Calculated Results:", {
      totalScore,
      correctAnswers,
      questionResults,
    });

    return {
      totalScore,
      correctAnswers,
      totalQuestions: quiz.questions.length,
      questionResults,
    };
  };

  // Submit quiz
  const handleSubmitQuiz = async (isAutoSubmit = false) => {
    if (submitting || submitted) return;

    const unansweredCount = quiz.questions.filter((q) => {
      if (q.question_type === "MATCHING") {
        const userMatches = matchingAnswers[q.position] || {};
        return Object.keys(userMatches).length < q.matching_pairs?.length;
      }
      return (
        !selectedAnswers[q.position] || selectedAnswers[q.position].length === 0
      );
    }).length;

    // Skip confirmation dialog if auto-submit
    if (!isAutoSubmit && unansweredCount > 0) {
      const confirmSubmit = window.confirm(
        `You have ${unansweredCount} unanswered question(s). Do you want to submit anyway?`,
      );
      if (!confirmSubmit) return;
    }

    setSubmitting(true);

    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("access_token");
      const calculatedResults = calculateScore();

      const submissionData = {
        quiz_id: parseInt(quiz_id),
        schedule_id: schedule_id,
        score: calculatedResults.totalScore,
      };

      const response = await fetch(`${API_URL}/quiz/submit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || `Submission failed! status: ${response.status}`,
        );
      }

      const result = await response.json();

      setResults({
        ...calculatedResults,
        attempt_id: result.attempt_id,
        passed: result.passed,
      });
      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      console.error("Error submitting quiz:", err);
      alert(`Failed to submit quiz: ${err.message}`);
      setSubmitting(false);
    }
  };

  // Navigation
  const handleNext = () => {
    if (selectedQuestion < quiz.questions.length) {
      setSelectedQuestion((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (selectedQuestion > 1) {
      setSelectedQuestion((prev) => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <div className="text-lg text-zinc-800">Loading quiz...</div>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-sm">
          <div className="text-lg text-red-600 font-semibold mb-4">
            {error || "Quiz not found"}
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-teal-400 text-white rounded hover:bg-teal-500 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (submitted && results) {
    const score = results.totalScore;
    const totalScore = quiz.total_points;
    const questions = results.correctAnswers;
    const totalQuestions = results.totalQuestions;
    const accuracy = Math.round(
      (results.correctAnswers / results.totalQuestions) * 100,
    );
    const attempts = 2;
    const percentage = Math.round((score / totalScore) * 100);
    const onRetake = () => window.location.reload();
    const onDashboard = () => navigate(-1);
    const onClose = () => navigate(-1);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="w-full max-w-[824px] h-auto bg-white rounded-lg shadow-lg relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="flex flex-col md:flex-row p-6 md:p-12 gap-8">
            {/* Left Side - Result Card */}
            <div className="w-full md:w-80 bg-slate-700 rounded-lg shadow-lg p-8 flex flex-col items-center justify-center">
              {/* Your Result Text */}
              <h3 className="text-white text-xl font-medium mb-8">
                Your Result
              </h3>

              {/* Circular Progress */}
              <div className="relative w-28 h-28 mb-8">
                {/* Background Circle */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="50"
                    stroke="#e5e7eb"
                    strokeWidth="10"
                    fill="none"
                  />
                  {/* Progress Circle */}
                  <circle
                    cx="56"
                    cy="56"
                    r="50"
                    stroke="#4AACC5"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={`${(percentage / 100) * 314} 314`}
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-out"
                  />
                </svg>
                {/* Percentage Text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">
                    {percentage}%
                  </span>
                </div>
              </div>

              {/* Great Work Text */}
              <h2 className="text-white text-2xl font-bold mb-3">
                Great Work!
              </h2>
              {/* Description */}
              <p className="text-white text-sm text-center leading-5">
                You've successfully finished the quiz with a high level of
                competence and clarity
              </p>
            </div>

            {/* Right Side - Summary Details */}
            <div className="flex-1 flex flex-col">
              {/* Summary Title */}
              <h2 className="text-stone-900 text-2xl font-bold text-center mb-8">
                Summary
              </h2>

              {/* Summary Items */}
              <div className="flex flex-col gap-2.5 mb-8">
                {/* Score */}
                <div className="w-full h-14 bg-red-50 rounded-lg shadow border flex items-center justify-between px-6">
                  <span className="text-stone-900 text-base font-medium">
                    Score
                  </span>
                  <span className="text-stone-900 text-base font-medium">
                    {score}{" "}
                    <span className="text-zinc-600">/ {totalScore}</span>
                  </span>
                </div>

                {/* Questions */}
                <div className="w-full h-14 bg-yellow-50 rounded-lg shadow border flex items-center justify-between px-6">
                  <span className="text-stone-900 text-base font-medium">
                    Questions
                  </span>
                  <span className="text-stone-900 text-base font-medium">
                    {questions < 10 ? `0${questions}` : questions}{" "}
                    <span className="text-zinc-600">/ {totalQuestions}</span>
                  </span>
                </div>

                {/* Accuracy */}
                {/* <div className="w-full h-14 bg-teal-50 rounded-lg shadow border flex items-center justify-between px-6">
                  <span className="text-stone-900 text-base font-medium">
                    Accuracy
                  </span>
                  <span className="text-stone-900 text-base font-medium">
                    {accuracy}%
                  </span>
                </div> */}

                {/* Attempts */}
                {/* <div className="w-full h-14 bg-violet-50 rounded-lg shadow border flex items-center justify-between px-6">
                  <span className="text-stone-900 text-base font-medium">
                    Attempts
                  </span>
                  <span className="text-stone-900 text-base font-medium">
                    {attempts}
                  </span>
                </div> */}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center items-center gap-3.5 mt-auto">
                <button
                  onClick={onRetake}
                  className="w-40 h-10 px-6 py-4 rounded border border-teal-400 flex justify-center items-center text-teal-400 text-base font-semibold hover:bg-teal-50 transition-colors"
                >
                  Retake Quiz
                </button>
                <button
                  onClick={onDashboard}
                  className="w-40 h-10 px-6 py-4 bg-cyan-600 rounded border border-cyan-600
                  flex justify-center items-center
                  text-white text-base font-semibold
                  hover:bg-cyan-700 transition-colors
                  whitespace-nowrap"
            >
                Go to Dashboard
               </button>


              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = quiz.questions.find(
    (q) => q.position === selectedQuestion,
  );
  const time = timeRemaining !== null ? formatTime(timeRemaining) : null;
  const totalPoints = quiz.questions.reduce((sum, q) => sum + q.points, 0);

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col">
      <div className="flex-1 px-3 sm:px-4 md:px-6 pb-6">
        <div
          className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col lg:flex-row h-full"
          style={{ minHeight: "calc(100vh - 180px)" }}
        >
          <div className="lg:w-2/5 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-200">
            <div className="bg-gray-50 p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-zinc-800">
                {quiz.title}
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                {quiz.questions.length} Questions
              </p>
            </div>

            <div className="overflow-y-auto flex-1 bg-gray-50">
              {quiz.questions.map((q) => {
                const isAnswered = isQuestionAnswered(
                  q.position,
                  q.question_type,
                );
                return (
                  <div
                    key={q.position}
                    className={`relative border-b border-slate-200 cursor-pointer transition-colors ${
                      selectedQuestion === q.position
                        ? "bg-slate-200"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                    onClick={() => setSelectedQuestion(q.position)}
                  >
                    {selectedQuestion === q.position && (
                      <div className="absolute left-0 top-0 w-1 h-full bg-teal-400"></div>
                    )}
                    <div className="p-3 sm:p-4 pl-4 sm:pl-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm sm:text-base font-medium text-zinc-800 flex-1 pr-3">
                          {q.question_text.length > 50
                            ? q.question_text.slice(0, 50) + "..."
                            : q.question_text}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-5 h-5 sm:w-6 sm:h-6 rounded shadow border flex items-center justify-center flex-shrink-0 ${
                              isAnswered
                                ? "bg-green-500 border-green-500"
                                : "bg-neutral-100 border-gray-200"
                            }`}
                          >
                            {isAnswered ? (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            ) : (
                              <span className="text-xs sm:text-sm font-medium text-zinc-800">
                                {q.position}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-zinc-600">
                        <span>{q.points} Points</span>
                        <div className="w-1 h-1 bg-zinc-600 rounded-full"></div>
                        <span className="capitalize">
                          {q.question_type.replace("_", " ")}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:w-3/5 flex flex-col">
            <div className="bg-neutral-100 border-b border-gray-200 p-4 sm:p-6 flex-shrink-0">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-stone-900">
                    {selectedQuestion}/{quiz.questions.length}
                  </div>
                  <div className="text-sm sm:text-base text-stone-900">
                    Questions
                  </div>
                </div>

                {time !== null && (
                  <div className="flex items-center gap-2">
                    <div className="bg-white rounded shadow border border-gray-200 p-2 sm:p-3 text-center">
                      <div
                        className={`text-xl sm:text-2xl font-semibold ${timeRemaining < 60 ? "text-red-600" : "text-stone-900"}`}
                      >
                        {String(time.minutes).padStart(2, "0")}
                      </div>
                      <div className="text-xs sm:text-sm text-stone-900">
                        Minutes
                      </div>
                    </div>
                    <div className="text-xl sm:text-2xl font-semibold text-stone-900">
                      :
                    </div>
                    <div className="bg-white rounded shadow border border-gray-200 p-2 sm:p-3 text-center">
                      <div
                        className={`text-xl sm:text-2xl font-semibold ${timeRemaining < 60 ? "text-red-600" : "text-stone-900"}`}
                      >
                        {String(time.seconds).padStart(2, "0")}
                      </div>
                      <div className="text-xs sm:text-sm text-stone-900">
                        Seconds
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-stone-900">
                    {totalPoints}
                  </div>
                  <div className="text-sm sm:text-base text-stone-900">
                    Overall Points
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 md:p-8 overflow-y-auto flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-stone-900 mb-6 sm:mb-8 leading-7 sm:leading-8">
                {currentQuestion.question_text}
              </h2>

              {currentQuestion.question_type === "MATCHING" &&
                currentQuestion.matching_pairs && (
                  <div className="space-y-4">
                    <p className="text-sm text-zinc-600 mb-4">
                      Match the items on the left with the correct items on the
                      right
                    </p>
                    {currentQuestion.matching_pairs.map((pair) => {
                      const shuffledOptions =
                        shuffledMatchingOptions[selectedQuestion] || [];
                      const selectedMatch =
                        matchingAnswers[selectedQuestion]?.[pair.position];

                      return (
                        <div
                          key={pair.position}
                          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
                        >
                          <div className="flex-1 bg-blue-50 border-2 border-blue-300 rounded-lg p-3 sm:p-4">
                            <p className="text-sm sm:text-base font-medium text-zinc-800">
                              {pair.left_text}
                            </p>
                          </div>

                          <div className="flex items-center justify-center">
                            <svg
                              className="w-6 h-6 text-gray-400 rotate-90 sm:rotate-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>

                          <div className="flex-1">
                            <select
                              value={selectedMatch || ""}
                              onChange={(e) =>
                                handleMatchingAnswer(
                                  selectedQuestion,
                                  pair.position,
                                  parseInt(e.target.value),
                                )
                              }
                              className="w-full bg-white border-2 border-gray-300 rounded-lg p-3 sm:p-4 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all"
                            >
                              <option value="">Select an option...</option>
                              {shuffledOptions.map((option) => (
                                <option
                                  key={option.position}
                                  value={option.position}
                                >
                                  {option.text}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              {currentQuestion.options &&
                currentQuestion.options.length > 0 && (
                  <div className="space-y-3 sm:space-y-4">
                    {currentQuestion.options.map((option) => {
                      const isSelected = selectedAnswers[
                        selectedQuestion
                      ]?.includes(option.position);
                      return (
                        <div
                          key={option.position}
                          className="flex items-center gap-3 sm:gap-4 cursor-pointer group"
                          onClick={() =>
                            toggleAnswer(selectedQuestion, option.position)
                          }
                        >
                          <div
                            className={`w-5 h-5 sm:w-6 sm:h-6 rounded shadow border flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSelected
                                ? "bg-green-500 border-green-500"
                                : "bg-gray-200 border-gray-300 group-hover:border-gray-400"
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 sm:w-4 sm:h-4 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="text-xs sm:text-sm text-neutral-700">
                            {option.option_text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

              {["SHORT_ANSWER", "FILL_IN_THE_BLANK"].includes(
                currentQuestion.question_type,
              ) && (
                <div>
                  <textarea
                    value={selectedAnswers[selectedQuestion]?.[0] || ""}
                    onChange={(e) =>
                      handleTextAnswer(selectedQuestion, e.target.value)
                    }
                    placeholder="Type your answer here..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            <div className="px-4 sm:px-6 md:px-8 py-4 border-t border-gray-200 flex-shrink-0 bg-white">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-center">
                <div className="text-sm text-zinc-600">
                  Progress:{" "}
                  {
                    quiz.questions.filter((q) =>
                      isQuestionAnswered(q.position, q.question_type),
                    ).length
                  }
                  /{quiz.questions.length} answered
                </div>
                <div className="flex gap-3 sm:gap-4 w-full sm:w-auto">
                  <button
                    className="flex-1 sm:flex-initial px-6 sm:px-8 py-2.5 sm:py-3 rounded border border-teal-400 text-teal-400 text-sm sm:text-base font-bold hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleBack}
                    disabled={selectedQuestion === 1}
                  >
                    Back
                  </button>
                  {selectedQuestion === quiz.questions.length ? (
                    <button
                      className="flex-1 sm:flex-initial px-6 sm:px-8 py-2.5 sm:py-3 rounded bg-green-600 border border-green-600 text-white text-sm sm:text-base font-bold hover:bg-green-700 transition-colors disabled:opacity-50"
                      onClick={handleSubmitQuiz}
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Quiz"}
                    </button>
                  ) : (
                    <button
                      className="flex-1 sm:flex-initial px-6 sm:px-8 py-2.5 sm:py-3 rounded
                      bg-cyan-600 border border-cyan-600
                      text-white text-sm sm:text-base font-bold
                      hover:bg-cyan-700 transition-colors"

                      onClick={handleNext}
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
